import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useTheme } from '../lib/theme';
import { Icon } from './Icon';

export interface ToastAction {
  label: string;
  onClick: () => void;
}

const ToastContext = createContext<(message: string, action?: ToastAction) => void>(
  () => undefined,
);

export function ToastProvider({ children }: { children: ReactNode }) {
  const { dz } = useTheme();
  const [message, setMessage] = useState<string | null>(null);
  const [action, setAction] = useState<ToastAction | null>(null);
  const timer = useRef<number | undefined>(undefined);

  /** With an action ("Desfazer") the toast stays 5 s. */
  const show = useCallback((m: string, a?: ToastAction) => {
    window.clearTimeout(timer.current);
    setMessage(m);
    setAction(a ?? null);
    timer.current = window.setTimeout(() => setMessage(null), a ? 5000 : 3500);
  }, []);

  useEffect(() => () => window.clearTimeout(timer.current), []);

  return (
    <ToastContext.Provider value={show}>
      {children}
      <div className={dz} style={{ background: 'transparent' }}>
        <div className="toastwrap" aria-live="polite">
          {message ? (
            <div className="toast" role="status">
              <Icon name="check" size={18} />
              {message}
              {action ? (
                <button
                  type="button"
                  className="btn sm"
                  style={{ background: 'transparent', color: '#FFF6EC', borderColor: '#5A5A5A' }}
                  onClick={() => {
                    action.onClick();
                    setMessage(null);
                  }}
                >
                  {action.label}
                </button>
              ) : null}
            </div>
          ) : null}
        </div>
      </div>
    </ToastContext.Provider>
  );
}

export const useToast = () => useContext(ToastContext);
